/**
 * postMessage protocol — the typed contract between the editor parent
 * frame and the preview iframe.
 *
 * Every message that crosses the iframe boundary is shaped by one of
 * these discriminated unions. The protocol is deliberately small: the
 * iframe is dumb, and the parent owns all editor state (history,
 * selection, persistence). The iframe only renders and reports clicks.
 *
 * Flow:
 *
 *   ┌────────────┐           ┌─────────────┐
 *   │  Parent    │           │  Iframe     │
 *   │ (editor)   │           │  (preview)  │
 *   └────────────┘           └─────────────┘
 *         │                         │
 *         │ ── ParentToIframe ──>   │   tree-update / set-selection /
 *         │                         │   set-tokens / set-viewport
 *         │                         │
 *         │ <── IframeToParent ──   │   ready / select / error
 *         │                         │
 *
 * Versioning: every message carries `protocol: 'oj-preview-v1'`. Old
 * iframes ignore unknown future versions; new parents detect a missing
 * `ready` ack and hard-reload the iframe (Phase 3 work). v1 is what
 * we ship; bumps are reserved for breaking changes only.
 *
 * Origin policy: the parent runs at app.outerjoyn.com; the iframe runs
 * at preview.outerjoyn.com (Phase 2 deploy) or about:srcdoc (dev).
 * Parent message handlers MUST validate `event.source` against the
 * iframe's contentWindow AND `event.origin` against the configured
 * preview origin. The iframe checks `event.source === window.parent`.
 * `validateMessageSource` below does the parent-side check; the iframe
 * runtime does the symmetric one.
 */

import type { SerializedLandingPageData } from '../serialization';
import type { BrandTokens, TokenId } from '../tokens/types';
import type { NodeId } from '../types';

// ─────────────────────────────────────────────────────────────────────────
// Protocol identity
// ─────────────────────────────────────────────────────────────────────────

/** Wire-format identifier baked into every message. Bump on breaking
 *  schema changes; non-breaking additions (new message types) do not
 *  bump. */
export const PROTOCOL_VERSION = 'oj-preview-v1' as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

// ─────────────────────────────────────────────────────────────────────────
// Parent → iframe
// ─────────────────────────────────────────────────────────────────────────

/** Push the entire tree to the iframe. The iframe re-compiles the HTML
 *  string and morphs the live DOM. Cheap because morphdom only patches
 *  what changed (typing in the inspector ≈ one text-node mutation). */
export interface TreeUpdateMessage {
  protocol: ProtocolVersion;
  type: 'tree-update';
  /** Serialized form (Maps → arrays). The iframe does not import the
   *  in-memory `LandingPageData` Map shape; it deserializes inline. */
  tree: SerializedLandingPageData;
  /** Optional brand tokens override. When omitted, the iframe keeps
   *  its current tokens (unset means "no change," not "use defaults"
   *  — defaults are applied on first `set-tokens` or via a fresh
   *  iframe boot). */
  tokens?: Partial<Record<TokenId, string>> | BrandTokens;
}

/** Tell the iframe which node is selected. Drives the selection
 *  outline overlay inside the iframe (selection chrome lives in the
 *  iframe, not on top — keeps coords correct under any scroll/zoom).
 *  `null` clears selection. */
export interface SetSelectionMessage {
  protocol: ProtocolVersion;
  type: 'set-selection';
  nodeId: NodeId | null;
}

/** Update brand tokens without touching the tree. Used by the brand
 *  wizard live-preview (Phase 3) and by the editor when a user toggles
 *  a per-page color override. */
export interface SetTokensMessage {
  protocol: ProtocolVersion;
  type: 'set-tokens';
  tokens: Partial<Record<TokenId, string>> | BrandTokens;
}

/** Resize the iframe document to the desktop/tablet/mobile preview
 *  viewport. The iframe sets a fixed body width + a media-query
 *  matcher so responsive primitives behave as if the viewport were
 *  that size. */
export interface SetViewportMessage {
  protocol: ProtocolVersion;
  type: 'set-viewport';
  viewport: 'desktop' | 'tablet' | 'mobile';
}

export type ParentToIframeMessage =
  | TreeUpdateMessage
  | SetSelectionMessage
  | SetTokensMessage
  | SetViewportMessage;

// ─────────────────────────────────────────────────────────────────────────
// Iframe → parent
// ─────────────────────────────────────────────────────────────────────────

/** Iframe boot complete. Sent once after the iframe runtime registers
 *  its message handler. The parent withholds `tree-update` calls until
 *  it sees a `ready` so the first paint isn't a no-op. */
export interface ReadyMessage {
  protocol: ProtocolVersion;
  type: 'ready';
  /** Iframe runtime version (independent of protocol version — lets us
   *  ship runtime patches without bumping the wire format). */
  runtimeVersion: string;
}

/** A user clicked something inside the iframe. The runtime walks up to
 *  the closest `[data-oj-id]` ancestor and posts that. The parent
 *  responds by sending `set-selection` back so the iframe paints the
 *  outline overlay — the iframe never paints selection on its own. */
export interface SelectMessage {
  protocol: ProtocolVersion;
  type: 'select';
  /** Null when the click landed outside any tree node (selection
   *  cleared). */
  nodeId: NodeId | null;
}

/** Iframe runtime crashed. Bubble up to the editor's error pill so the
 *  user knows the preview is stale instead of silently freezing. */
export interface ErrorMessage {
  protocol: ProtocolVersion;
  type: 'error';
  /** Short user-facing label. */
  code:
    | 'compile-failed'
    | 'morph-failed'
    | 'invalid-message'
    | 'unknown';
  /** Verbose diagnostic for the editor's logs. Not shown to users. */
  detail: string;
}

export type IframeToParentMessage = ReadyMessage | SelectMessage | ErrorMessage;

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Type-guard: is this `unknown` (e.g. `event.data` from the wire) a
 * well-formed message of either direction? Use at the boundary before
 * dispatching. Cheap structural check — full schema validation is
 * intentionally deferred to the dispatch site so we can return
 * targeted errors per message type.
 */
export function isProtocolMessage(value: unknown): value is { protocol: ProtocolVersion; type: string } {
  if (!value || typeof value !== 'object') return false;
  const v = value as { protocol?: unknown; type?: unknown };
  return v.protocol === PROTOCOL_VERSION && typeof v.type === 'string';
}

/**
 * Parent-side source validator. The browser routes every postMessage
 * to every iframe in the page; the parent must check that the message
 * came from *its* iframe before trusting it. Pass the configured
 * preview origin (defaults to the iframe's own origin in dev).
 *
 * Returns true iff the event is from the expected iframe AND from an
 * allowed origin.
 */
export function validateMessageSource(
  event: MessageEvent,
  expected: { iframeWindow: Window | null; expectedOrigin: string },
): boolean {
  if (!expected.iframeWindow) return false;
  if (event.source !== expected.iframeWindow) return false;
  // `expectedOrigin === '*'` is dev-only — allow it as an explicit
  // opt-out for local file:// or about:srcdoc previews where the
  // origin is "null". Production callers must pass the real origin.
  //
  // Even with `'*'` we tighten one degenerate case: if the iframe was
  // navigated away to a different web origin mid-session, its
  // `event.origin` would be that new origin. We reject anything that
  // isn't the well-known "no origin" sentinel `'null'` (srcdoc /
  // file://) OR the parent's own origin (same-origin srcdoc). This
  // closes finding #27 from the audit — `'*'` shouldn't mean "trust
  // any origin", it means "trust dev's null-origin srcdoc".
  if (expected.expectedOrigin === '*') {
    const parentOrigin =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : null;
    const acceptable = new Set(['null', '']);
    if (parentOrigin) acceptable.add(parentOrigin);
    if (!acceptable.has(event.origin)) return false;
    return true;
  }
  if (event.origin !== expected.expectedOrigin) {
    return false;
  }
  return true;
}
